import { gql, TypedDocumentNode } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import type { UniversityStudentsQuery } from "../students/types";

export type UniversityRosterUploadNode = {
  id: string;
  createdAt: string;
  programLevel?: string | null;
  uploadedBy: { id: string; name: string } | null;
  file?: { filename: string; url: string | null } | null;
};

export type UniversityRosterUploadsQuery = {
  universities: Array<{
    id: string;
    rosterUploads: {
      totalCount: number;
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor?: string;
        endCursor?: string;
      };
      nodes: Array<UniversityRosterUploadNode | null>;
    };
  }>;
};

export type UniversityRosterUploadsQueryVariables = {
  legacyId: string;
  first?: number;
  after?: string;
  last?: number;
  before?: string;
};

export const UNIVERSITY_ROSTER_UPLOADS_QUERY: TypedDocumentNode<
  UniversityRosterUploadsQuery,
  UniversityRosterUploadsQueryVariables
> = gql`
  query getUniversityRosterUploads($legacyId: String!, $first: Int, $after: String, $last: Int, $before: String) {
    universities(legacyId: $legacyId) {
      id
      rosterUploads(first: $first, after: $after, last: $last, before: $before) {
        nodes {
          id
          createdAt
          programLevel
          uploadedBy {
            id
            name
          }
          file {
            filename
            url
          }
        }
        ...PageInfo
      }
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

/** Semester report only — includes cohort context not present on shared STUDENTS_QUERY. */
export const STUDENTS_SEMESTER_REPORT_QUERY: TypedDocumentNode<UniversityStudentsQuery> = gql`
  query getUniversityStudentsSemesterReport(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeUniversityStudentsInput
  ) {
    universityStudents(first: $first, last: $last, before: $before, after: $after, input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        firstName
        lastName
        displayName
        pgaId
        email
        gender
        profilePhoto
        educationLatestPgmProgram {
          startDate
          level
          levelCode
          curriculumVersion
          completed
          programType
        }
        educationPgmProgramHistory {
          level
          levelCode
          startDate
          endDate
          completed
        }
        prospectProgram {
          id
          playingAbilityTestStatus {
            status
            completed
            expired
            startDate
            expirationDate
            submissions {
              id
              date
              result
              patType
              roundOneScore
              roundTwoScore
              roundOneId
              roundTwoId
              totalScore
              targetScore
              additionalStatus
              note
              slug
              name
              suspension {
                startDate
                endDate
              }
            }
            activeSuspension {
              startDate
              endDate
            }
          }
        }
        university {
          id
          name
        }
        universityCohort {
          id
          name
        }
        universityCohortContext {
          inOriginalCohort
          originalCohort {
            id
            name
          }
          currentCohort {
            id
            name
          }
        }
        totalInternshipMonths
      }
    }
  }
`;
